// ★★★ PC-6 — POSSE DO LEAD: de quem é, e quem pode assumir. Puro.
//
// ⚠⚠ ESPELHO: `functions/leads/posse.js` tem o MESMO corpo (o deploy das Functions
// empacota só `functions/`, então um lado não importa o outro). Este é a fonte da
// verdade; `posse.espelho.test.js` passa as mesmas entradas pelos dois e exige
// saída idêntica. Quem mexer num, mexe no outro.
//
// ⚠⚠ SEM IMPORT de propósito: o arquivo é autocontido para os dois lados serem
// cópia literal (o web não exporta `partesNoFuso`; o servidor não tem `@/`).
//
// A regra é do dono (24/10/2026) — `docs/pc6-plano.md` §1. A PRIMEIRA linha que
// casa vence:
//
//   1  seu                     o lead é de quem pergunta
//   2  fechou                  etapa fechado, valorFechado > 0 ou recebimento   → travado para sempre
//   3  compareceu              compareceuEm, agendamento compareceu, etapa compareceu/nao_fechou
//   4  consulta_futura         agendamento `agendado` com data no futuro         → travado
//   5  consulta_sem_baixa      agendamento `agendado` já passado, DESTE mês (N10) → travado
//                              (de mês anterior não trava por isso — decisão do dono, 24/10)
//   6  agendado_sem_data       etapa Agendado sem consulta registrada            → travado
//   7  sem_sdr                 nenhuma das acima e sem SDR                       → liberado ("assuma e agende")
//   8  reserva_vigente/vencida `posseReservadaAte` (rodízio)                     → travado / liberado
//   9  mes_atual               `criadoEm` no mês corrente de SP                  → travado até o fim do mês
//   9b faltou_no_mes           mês anterior + faltou a consulta DESTE mês (A3)   → travado até o fim do mês
//      (N11 revisto, dono 24/10: assumir lead de mês anterior NÃO trava — só agendar/comparecer)
//  10  mes_anterior_faltou     mês anterior + faltou                             → liberado
//  11  mes_anterior_sem_agenda mês anterior + nunca agendou (ou só cancelado)    → liberado
//
// ★★★ Ajuste do dono de 24/10 — por que 2–6 vêm ANTES de "sem SDR": um lead sem SDR
// (indicação, fachada, cadastrado pela recepção) que já agendou, compareceu ou
// fechou não foi influenciado por SDR nenhuma. Se ficasse liberado, a primeira que
// clicasse "Assumir" levaria uma venda que não fez. Só o gestor atribui esse lead.
//
// ★ E por que 2–6 vêm antes da reserva (8): se a dona agendou dentro do prazo sem
// clicar "Atender", a consulta já trava o lead com ela; a reserva vencida não
// apaga esse fato.

export const FUSO_POSSE = 'America/Sao_Paulo';

const PAPEIS_GESTOR = ['gestor', 'superadmin'];

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Data civil (`AAAA-MM-DD`), mês (`AAAA-MM`) e minutos do dia de um instante, no fuso.
 * ⚠ `formatToParts` no fuso PEDIDO — nunca `getMonth`/`getHours`, que respondem no fuso do
 * PROCESSO (UTC numa Cloud Function): é o defeito de 3 h da A-2.
 */
function partes(instante, fuso) {
  const p = {};
  for (const parte of new Intl.DateTimeFormat('en-CA', {
    timeZone: fuso,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(instante)) p[parte.type] = parte.value;
  return {
    ano: Number(p.year),
    mes: Number(p.month),
    dia: `${p.year}-${p.month}-${p.day}`,
    minutosDoDia: (Number(p.hour) % 24) * 60 + Number(p.minute),
  };
}

/** O instante absoluto de uma data civil + minutos do dia, no fuso (duas passadas: horário de verão). */
export function instanteNoFuso(dia, minutosDoDia, fuso = FUSO_POSSE) {
  const [a, m, d] = String(dia).split('-').map(Number);
  const alvo = Date.UTC(a, m - 1, d) + minutosDoDia * 60000;
  let t = alvo;
  for (let i = 0; i < 2; i += 1) {
    const lido = partes(new Date(t), fuso);
    const [la, lm, ld] = lido.dia.split('-').map(Number);
    t += alvo - (Date.UTC(la, lm - 1, ld) + lido.minutosDoDia * 60000);
  }
  return new Date(t);
}

/** 00:00 do dia 1º do mês de `agora`, no fuso — o começo do "mês atual" das regras 1 e 3. */
export function inicioDoMes(agora, fuso = FUSO_POSSE) {
  const { ano, mes } = partes(agora, fuso);
  return instanteNoFuso(`${ano}-${String(mes).padStart(2, '0')}-01`, 0, fuso);
}

/** 00:00 do dia 1º do mês SEGUINTE — onde a trava do mês atual acaba. */
function inicioDoMesSeguinte(agora, fuso) {
  const { ano, mes } = partes(agora, fuso);
  const [a, m] = mes === 12 ? [ano + 1, 1] : [ano, mes + 1];
  return instanteNoFuso(`${a}-${String(m).padStart(2, '0')}-01`, 0, fuso);
}

const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const hhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const ddmm = (dia) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/** "09:00" no mesmo dia de `agora`; "seg 09:00" em outro dia. */
function rotuloHora(instante, agora, fuso) {
  const p = partes(instante, fuso);
  if (p.dia === partes(agora, fuso).dia) return hhmm(p.minutosDoDia);
  const [a, m, d] = p.dia.split('-').map(Number);
  return `${DIAS_CURTOS[new Date(Date.UTC(a, m - 1, d)).getUTCDay()]} ${hhmm(p.minutosDoDia)}`;
}

/** "30/10 às 14:00". */
function rotuloConsulta(instante, fuso) {
  const p = partes(instante, fuso);
  return `${ddmm(p.dia)} às ${hhmm(p.minutosDoDia)}`;
}

/** "31/10" — o último dia do mês de `agora`. */
function rotuloFimDoMes(agora, fuso) {
  const ultimo = new Date(inicioDoMesSeguinte(agora, fuso).getTime() - 60000);
  return ddmm(partes(ultimo, fuso).dia);
}

const ETAPAS_COMPARECEU = ['compareceu', 'nao_fechou'];

/**
 * @param lead     o documento do lead (com `id`)
 * @param contexto `{ agendamentos, temRecebimento, agora, fuso, quem: { id, papel } }`
 *                 ⚠ `agora` por PARÂMETRO (regra da WN): a função não lê relógio nenhum.
 * @returns `{ estado: 'seu'|'travado'|'liberado', motivo, texto, ate, dona, podeAssumir, porCimaDaTrava }`
 */
export function situacaoDePosse(lead, {
  agendamentos = [],
  temRecebimento = false,
  agora,
  fuso = FUSO_POSSE,
  quem = {},
} = {}) {
  const instante = paraData(agora);
  if (!instante) throw new Error('situacaoDePosse: `agora` é obrigatório');

  const dona = lead?.sdrId ? { id: lead.sdrId, nome: lead.sdrNome || '' } : null;
  const nome = dona?.nome || 'outra SDR';
  const doLead = (agendamentos ?? []).filter((a) => !a?.leadId || a.leadId === lead?.id);
  const comStatus = (s) => doLead.filter((a) => a?.status === s);

  const resultado = (estado, motivo, texto, ate = null) => {
    const gestor = PAPEIS_GESTOR.includes(quem?.papel);
    const porCimaDaTrava = gestor && estado === 'travado';
    return {
      estado,
      motivo,
      texto: porCimaDaTrava ? `${texto} Você é gestor: pode transferir mesmo assim.` : texto,
      ate,
      dona,
      podeAssumir: gestor || estado === 'liberado',
      porCimaDaTrava,
    };
  };
  /** Trava permanente: com dona, "Travado com X: …"; sem dona, só o gestor atribui. */
  const trava = (motivo, comDona, semDona, ate = null) => resultado(
    'travado',
    motivo,
    dona ? `Travado com ${nome}: ${comDona}.` : `Lead sem SDR que ${semDona} — só o gestor pode atribuir.`,
    ate,
  );

  // 1 — seu
  if (dona && quem?.id && dona.id === quem.id) return resultado('seu', 'seu', 'Este lead é seu.');

  // 2 — fechou (D3 + N7)
  if (lead?.etapa === 'fechado' || Number(lead?.valorFechado) > 0 || temRecebimento === true) {
    return trava('fechou', 'o lead fechou', 'já fechou');
  }

  // 3 — compareceu (D3, D6)
  if (paraData(lead?.compareceuEm) || comStatus('compareceu').length > 0 || ETAPAS_COMPARECEU.includes(lead?.etapa)) {
    return trava('compareceu', 'o paciente já compareceu', 'já compareceu');
  }

  // 4 e 5 — consulta marcada (D4). Cancelado não conta.
  const marcadas = comStatus('agendado').map((a) => paraData(a.dataHora));
  const futuras = marcadas.filter((d) => d && d > instante).sort((x, y) => x - y);
  if (futuras.length > 0) {
    const quando = rotuloConsulta(futuras[0], fuso);
    return trava('consulta_futura', `consulta marcada para ${quando}`, `tem consulta marcada para ${quando}`, futuras[0]);
  }
  // N10 (decisão do dono, 24/10: "Trava se for deste mês, libera se antiga") — consulta
  // passada que ninguém baixou. DESTE mês (SP) é baixa pendente da recepção: o paciente pode
  // ter comparecido sem registro, e liberar entregaria a venda a outra SDR. De mês ANTERIOR
  // (quase todas da importação da planilha, medido) não trava por este motivo: o lead segue
  // as linhas de baixo (sem SDR / mês atual / faltou / nunca agendou).
  const inicioDoMesAtual = inicioDoMes(instante, fuso);
  const passadasDoMes = marcadas.filter((d) => d && d >= inicioDoMesAtual).sort((x, y) => y - x);
  if (passadasDoMes.length > 0) {
    const quando = ` de ${ddmm(partes(passadasDoMes[0], fuso).dia)}`;
    return trava('consulta_sem_baixa', `consulta${quando} ainda sem baixa`, `já agendou (consulta${quando} sem baixa)`);
  }
  if (marcadas.some((d) => !d)) {
    // `agendado` sem data: não há como saber se é deste mês — trava, como a etapa Agendado sem data.
    return trava('agendado_sem_data', 'etapa Agendado, sem consulta registrada', 'já agendou');
  }

  // 6 — etapa Agendado sem NENHUM documento agendado/faltou/compareceu (os importados — D6).
  // ⚠ "nenhum agendado" inclui o antigo sem baixa: com documento, a linha não casa (plano §1b).
  if (lead?.etapa === 'agendado' && marcadas.length === 0 && comStatus('faltou').length === 0) {
    return trava('agendado_sem_data', 'etapa Agendado, sem consulta registrada', 'já agendou');
  }

  // 7 — sem SDR, e nunca agendou com sucesso (ou só cancelado/faltou)
  if (!dona) return resultado('liberado', 'sem_sdr', 'Lead sem SDR responsável — assuma e agende.');

  // 8 — reserva do rodízio. A borda pertence a quem chega depois (como `reservaVigente`).
  const reserva = paraData(lead?.posseReservadaAte);
  if (reserva) {
    const ate = rotuloHora(reserva, instante, fuso);
    if (instante < reserva) {
      return resultado('travado', 'reserva_vigente', `Reservado para ${nome} até ${ate} — ainda não atendeu.`, reserva);
    }
    return resultado('liberado', 'reserva_vencida', `${nome} não atendeu até ${ate} — você pode assumir.`);
  }

  // 9 — mês atual (regras 1 e 3). `criadoEm` nulo é gravação pendente: conta como agora.
  // ★★ N11 REVISTO (dono, 24/10/A3): o mês é SÓ o do `criadoEm`. "Se a pessoa assume um lead de
  // agosto, ele só trava se ela conseguiu agendar" — assumir um lead de mês anterior NÃO trava
  // (outra SDR pode assumir de novo; as tarefas vão junto, com trilha). O que trava esse lead é
  // agendar (linhas 4–5) ou comparecer/fechar (2–3). `sdrAssumidoEm` não entra nesta conta.
  const faltas = comStatus('faltou');
  const faltou = faltas.length > 0 || lead?.etapa === 'nao_compareceu';
  const criado = paraData(lead?.criadoEm) ?? instante;
  if (criado >= inicioDoMes(instante, fuso)) {
    const sufixo = faltou ? ' (faltou à consulta)' : '';
    return resultado(
      'travado',
      'mes_atual',
      `Travado com ${nome} até ${rotuloFimDoMes(instante, fuso)}: lead deste mês${sufixo}.`,
      inicioDoMesSeguinte(instante, fuso),
    );
  }

  // 9b — ★ interpretação do revisor (A3, o dono pode corrigir): lead de mês ANTERIOR que foi
  // agendado e FALTOU a uma consulta DESTE mês fica travado com a dona até o fim do mês da
  // consulta (a regra 3 aplicada ao mês do agendamento); depois volta a liberado (linha 10).
  const faltasDoMes = faltas.map((a) => paraData(a.dataHora)).filter((d) => d && d >= inicioDoMes(instante, fuso));
  if (faltasDoMes.length > 0) {
    const quando = ddmm(partes(faltasDoMes.sort((x, y) => y - x)[0], fuso).dia);
    return resultado(
      'travado',
      'faltou_no_mes',
      `Travado com ${nome} até ${rotuloFimDoMes(instante, fuso)}: faltou à consulta de ${quando}.`,
      inicioDoMesSeguinte(instante, fuso),
    );
  }

  // 10 — mês anterior + faltou (regra 3)
  if (faltou) return resultado('liberado', 'mes_anterior_faltou', 'Liberado: faltou à consulta e é de mês anterior.');

  // 11 — mês anterior + nunca agendou (regra 2)
  return resultado('liberado', 'mes_anterior_sem_agenda', 'Liberado: lead de mês anterior que nunca agendou.');
}
